import React, { useEffect, useState } from 'react';
import { ActionType } from '@common/enums/command-hub/command-buttons';
import CustomSyncIcon from '../misc/CustomSyncIcon';
import { useTranslation } from 'react-i18next';
import SyncStatus from '../../models/enums/sync-status';
import { selectRobotsData } from '../../store/robots/robots-slice-selectors';
import { useAppDispatch, useAppSelector } from '../../store';
import MessageType from '@common/enums/robot/rsw/message-types';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { sendRobotWebsocketMessage } from 'store/middleware/robot-websocket-middleware';
import { trackEvent } from 'services/analytics';
import { AnalyticsEvent, RobotAccessAction } from 'services/analytics-events';

const MissionSyncButton: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const currentRobotId = robotsData.currentRobot?.id ?? null;

  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [isTimeoutReached, setIsTimeoutReached] = useState(false);

  useEffect(() => {
    if (robotsData.currentRobotData.statusTimestamp != null) {
      setIsSyncInProgress(false);
      setIsTimeoutReached(false);
    }
  }, [robotsData.currentRobotData.statusTimestamp]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isSyncInProgress) {
      timer = setTimeout(() => {
        setIsTimeoutReached(true);
        trackEvent(AnalyticsEvent.RobotSyncTimedOut, {
          sync_type: RobotAccessAction.SyncOpenTasks,
          robot_id: currentRobotId,
        });
        setIsSyncInProgress(false);
      }, 10000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isSyncInProgress, currentRobotId]);

  if (robotsData.currentRobot == null || robotsData.currentRobot.status == null) return null;

  const syncMissionsClickHandler = () => {
    trackEvent(AnalyticsEvent.RobotAccessUsed, {
      access_action: RobotAccessAction.SyncOpenTasks,
      robot_id: robotsData.currentRobot?.id ?? null,
      robot_serial: robotsData.currentRobot?.serialNumber ?? null,
    });
    setIsSyncInProgress(true);
    dispatch(
      sendRobotWebsocketMessage({
        type: MessageType.Actions,
        message: { action: ActionType.ForceSync },
      }),
    );
  };

  return (
    <Tooltip
      title={
        isTimeoutReached
          ? t('info-tooltip-robot-sync-timeout-reached')
          : !robotsData.currentRobot.status.active
            ? t('info-tooltip-robot-sync-robot-offline')
            : robotsData.currentRobot.status.unsentMissions === 0
              ? t('info-tooltip-robot-sync-unnecessary')
              : robotsData.currentRobot.status.unsentMissions == null
                ? t('info-tooltip-robot-sync-not-enabled')
                : t('info-tooltip-robot-sync')
      }
    >
      <span>
        <Button
          disabled={isSyncInProgress}
          fullWidth
          variant="outlined"
          onClick={syncMissionsClickHandler}
          sx={(theme) => ({
            color: isTimeoutReached
              ? theme.palette.error.main
              : !robotsData.currentRobot!.status!.active ||
                  robotsData.currentRobot!.status!.unsentMissions === 0 ||
                  robotsData.currentRobot!.status!.unsentMissions == null
                ? theme.palette.action.disabled
                : theme.palette.info.main,
          })}
          startIcon={
            <CustomSyncIcon
              status={
                isSyncInProgress
                  ? SyncStatus.IN_PROGRESS
                  : isTimeoutReached
                    ? SyncStatus.FAILED
                    : SyncStatus.SUCCEEDED
              }
            />
          }
        >
          {t('button-sync')}
        </Button>
      </span>
    </Tooltip>
  );
};

export default MissionSyncButton;
